import { Link } from "@inertiajs/react"

import { CreatorMasterclassStatusPill } from "@/components/learn/creator-masterclass-status-pill"
import { MasterclassCover } from "@/components/learn/masterclass-cover"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { learnMasterclassPath } from "@/routes"
import type { CreatorLearnMasterclassRow } from "@/types/learn"

/**
 * OKS-56 / OKS-836 — Creator-side masterclass card (mobile-first
 * single-column list — UX Spec §2).
 *
 * OKS-836 redesigned this from a horizontal 56px left-thumb layout into
 * a vertical banner card mirroring the brand-side
 * `masterclasses/masterclass-card.tsx`: a full-width `size="card"` cover
 * banner (locked to the shared 3:2 `MASTERCLASS_COVER_ASPECT_CLASS` — the
 * resolved `:card` variant when a cover is attached, else the
 * deterministic `MasterclassSwatch` at the same 3:2 footprint) → title →
 * topic → 2-line description → the creator-only completion status pill in
 * a footer row. The list now shares the detail hero's frame
 * (framed==shown); the square-thumb derivation was retired for this
 * surface (see `lib/masterclass-cover`).
 *
 * Whole card is an Inertia `<Link>` to `learnMasterclassPath(id)` —
 * semantic anchor wrap (UX Spec §6 a11y), not div+onClick.
 */
export interface CreatorMasterclassCardProps {
  masterclass: CreatorLearnMasterclassRow
  completed: boolean
}

export function CreatorMasterclassCard({
  masterclass,
  completed,
}: CreatorMasterclassCardProps) {
  return (
    <Card
      className="group focus-within:ring-ring flex flex-col gap-0 overflow-hidden p-0 transition-shadow focus-within:ring-2 hover:shadow-md"
      data-testid={`learn-card-${masterclass.id}`}
    >
      <Link
        href={learnMasterclassPath(masterclass.id)}
        className="flex h-full flex-col focus:outline-none"
        aria-label={masterclass.title}
      >
        <MasterclassCover
          id={masterclass.id}
          coverImageUrl={masterclass.cover_image_url}
          title={masterclass.title}
          size="card"
          data-testid={`learn-card-${masterclass.id}-cover`}
        />

        <CardContent className="flex flex-col gap-1 p-4">
          <h3
            className="line-clamp-2 text-base leading-tight font-semibold tracking-tight"
            data-testid={`learn-card-${masterclass.id}-title`}
          >
            {masterclass.title}
          </h3>
          <p className="text-muted-foreground line-clamp-1 text-xs">
            {masterclass.topic}
          </p>
          <p className="text-muted-foreground line-clamp-2 text-sm">
            {masterclass.description}
          </p>
        </CardContent>

        <CardFooter className="flex items-center justify-between gap-2 p-4 pt-0">
          <CreatorMasterclassStatusPill
            completed={completed}
            data-testid={`learn-card-${masterclass.id}-status`}
          />
        </CardFooter>
      </Link>
    </Card>
  )
}
